import ENS_PUBLIC_RESOLVER_ABI from 'abis/ens-public-resolver.json'
import ENS_ABI from 'abis/ens-registrar.json'
import { WAGMI_REFETCH_POLICY_NEVER } from 'config/RefetchPolicies'
import { ENS_REGISTRAR_ADDRESSES } from 'constants/addresses'
import { useMemo } from 'react'
import type { Abi, Address } from 'viem'
import { namehash } from 'viem/ens'
import { useReadContract } from 'wagmi'

import { isAddress } from '../utils'
import isZero from '../utils/isZero'
import { useConnection } from './useConnection'
import useDebounce from './useDebounce'
import useENSAddress from './useENSAddress'

/**
 * Does a reverse lookup for an address to find its ENS name.
 * Note this is not the same as looking up an ENS name to find an address.
 */
export default function useENSName(address?: string): { ENSName: string | null; loading: boolean } {
  const { chainId } = useConnection()

  const debouncedAddress = useDebounce(address, 200)
  const ensNodeArgument = useMemo(() => {
    if (!debouncedAddress || !isAddress(debouncedAddress)) return undefined
    try {
      const reverseName = `${debouncedAddress.toLowerCase().substring(2)}.addr.reverse`
      return namehash(reverseName) as Address
    } catch {
      return undefined
    }
  }, [debouncedAddress])

  const registrarAddress = ENS_REGISTRAR_ADDRESSES[chainId]
  // Step 1: Get resolver address from ENS registrar
  const { data: resolverAddressResult, isLoading: resolverLoading } = useReadContract({
    address: registrarAddress,
    abi: ENS_ABI as Abi,
    functionName: 'resolver',
    args: ensNodeArgument ? [ensNodeArgument] : undefined,
    query: {
      ...WAGMI_REFETCH_POLICY_NEVER,
      enabled: !!ensNodeArgument && !!registrarAddress,
    },
  })

  const resolverAddress = useMemo(() => {
    if (!resolverAddressResult || isZero(resolverAddressResult as string)) return undefined
    return resolverAddressResult as Address
  }, [resolverAddressResult])

  // Step 2: Get name from resolver
  const { data: nameResult, isLoading: nameLoading } = useReadContract({
    address: resolverAddress,
    abi: ENS_PUBLIC_RESOLVER_ABI as Abi,
    functionName: 'name',
    args: ensNodeArgument ? [ensNodeArgument] : undefined,
    query: {
      ...WAGMI_REFETCH_POLICY_NEVER,
      enabled: !!resolverAddress && !!ensNodeArgument,
    },
  })

  const name = nameResult as string

  // ENS does not enforce that an address owns a .eth domain before setting it as a reverse proxy
  // and recommends that you perform a match on the forward resolution
  // see: https://docs.ens.domains/dapp-developer-guide/resolving-names#reverse-resolution
  //
  const fwdAddr = useENSAddress(name)
  const checkedName = address === fwdAddr?.address ? name : null

  const changed = debouncedAddress !== address
  return useMemo(
    () => ({
      ENSName: changed ? null : checkedName ?? null,
      loading: changed || resolverLoading || nameLoading,
    }),
    [changed, nameLoading, checkedName, resolverLoading]
  )
}
